"use client";

import * as React from "react";
import { Calendar as CalendarIcon } from "lucide-react";
import type { DateRange, Matcher } from "react-day-picker";
import { cn } from "../utils";
import { RequiredMark } from "../required-mark";
import { Calendar } from "../calendar";
import { Popover, PopoverAnchor, PopoverContent } from "../popover";
export interface InputDateRangeProps {
  name: string;
  id?: string;
  label?: string | null;
  /** Helper text rendered below the field — plain string or rich content (e.g. a description with a link) */
  description?: React.ReactNode;
  startPlaceholder?: string;
  endPlaceholder?: string;
  /** Combined value as `"YYYY-MM-DD/YYYY-MM-DD"`. Empty string when either side is unset. */
  value?: string;
  /** Fires when the range changes; emits `""` while the range is incomplete. */
  onChange?: (range: string) => void;
  onBlur?: () => void;
  required?: boolean;
  error?: string;
  minDate?: string | null;
  maxDate?: string | null;
  disabled?: boolean;
  className?: string;
  /** Number of months to show in the calendar popover. Default 2 (matches shadcn). */
  numberOfMonths?: number;
}
function toDate(iso: string): Date {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y ?? 0, (m ?? 1) - 1, d ?? 1);
}
function toISO(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}
function formatDisplay(iso: string): string {
  return toDate(iso).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric"
  });
}
function parseRange(value: string | undefined): {
  start: string | null;
  end: string | null;
} {
  if (!value) return {
    start: null,
    end: null
  };
  const [start = "", end = ""] = value.split("/");
  return {
    start: /^\d{4}-\d{2}-\d{2}$/.test(start) ? start : null,
    end: /^\d{4}-\d{2}-\d{2}$/.test(end) ? end : null
  };
}
export const InputDateRange = React.forwardRef<HTMLDivElement, InputDateRangeProps>(({
  name,
  id,
  label,
  description,
  startPlaceholder = "Start Date",
  endPlaceholder = "End Date",
  value,
  onChange,
  onBlur,
  required,
  error,
  minDate,
  maxDate,
  disabled,
  className,
  numberOfMonths = 2
}, ref) => {
  const generatedId = React.useId();
  const baseId = id || name || generatedId;
  const startId = `${baseId}-start`;
  const endId = `${baseId}-end`;
  const descriptionId = `${baseId}-description`;
  const startBtnRef = React.useRef<HTMLButtonElement | null>(null);
  const endBtnRef = React.useRef<HTMLButtonElement | null>(null);
  const {
    start,
    end
  } = parseRange(value);
  const [open, setOpen] = React.useState(false);
  const [lastFocused, setLastFocused] = React.useState<"start" | "end">("start");
  const hasError = Boolean(error);
  const hasDescription = Boolean(error || description);
  const selected = React.useMemo<DateRange | undefined>(() => {
    if (!start && !end) return undefined;
    return {
      from: start ? toDate(start) : undefined,
      to: end ? toDate(end) : undefined
    };
  }, [start, end]);
  const defaultMonth = start ? toDate(start) : minDate ? toDate(minDate) : undefined;
  const disabledMatcher = React.useMemo<Matcher[] | undefined>(() => {
    const matchers: Matcher[] = [];
    if (minDate) matchers.push({
      before: toDate(minDate)
    });
    if (maxDate) matchers.push({
      after: toDate(maxDate)
    });
    return matchers.length ? matchers : undefined;
  }, [minDate, maxDate]);
  const openPopover = (focused: "start" | "end") => {
    setLastFocused(focused);
    setOpen(true);
  };
  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) onBlur?.();
  };
  const handleSelect = (range: DateRange | undefined) => {
    const fromIso = range?.from ? toISO(range.from) : "";
    const toIso = range?.to ? toISO(range.to) : "";
    onChange?.(fromIso && toIso ? `${fromIso}/${toIso}` : "");
    // Stay open so the user can keep adjusting the range. Dismiss happens
    // on outside-click, Escape, or by opening another field.
  };
  const triggerChrome = (active: boolean) => cn("flex h-12 flex-1 items-center justify-start rounded-sm border border-transparent px-4 py-3", "typography-body-sm-regular text-left", "bg-input-surface-rest", "outline-none transition-colors", "hover:bg-input-surface-hover", "focus:border-border-subtle focus:bg-input-surface-hover", active && "border-border-subtle bg-input-surface-hover", hasError && "bg-input-surface-error hover:bg-input-surface-error focus:bg-input-surface-error", disabled && "pointer-events-none bg-input-surface-disabled text-input-text-disabled");
  return <div ref={ref} data-testid="input-date-range" className={cn("group/input-date-range flex w-full flex-col gap-2", className)}>
        {Boolean(label) && <label htmlFor={startId} className={cn("typography-component-sm-medium desktop:typography-component-md-medium", disabled ? "text-input-text-disabled" : "text-input-text-label")}>
            {label}
            <RequiredMark required={required} />
          </label>}

        <Popover open={open} onOpenChange={handleOpenChange}>
          <PopoverAnchor asChild>
            <div className="flex w-full items-stretch gap-x-6" aria-describedby={hasDescription ? descriptionId : undefined}>
              <button ref={startBtnRef} id={startId} type="button" disabled={disabled} aria-haspopup="dialog" aria-expanded={open} aria-invalid={hasError || undefined} aria-label={startPlaceholder} data-empty={!start || undefined} onClick={() => openPopover("start")} className={cn(triggerChrome(open && lastFocused === "start"), "data-[empty=true]:text-input-text-disabled", "text-input-text-rest")}>
                <span className="truncate">
                  {start ? formatDisplay(start) : startPlaceholder}
                </span>
              </button>
              <button ref={endBtnRef} id={endId} type="button" disabled={disabled} aria-haspopup="dialog" aria-expanded={open} aria-invalid={hasError || undefined} aria-label={endPlaceholder} data-empty={!end || undefined} onClick={() => openPopover("end")} className={cn(triggerChrome(open && lastFocused === "end"), "data-[empty=true]:text-input-text-disabled", "text-input-text-rest", "justify-between gap-3")}>
                <span className="truncate">
                  {end ? formatDisplay(end) : endPlaceholder}
                </span>
                <CalendarIcon aria-hidden className={cn("size-5 shrink-0", hasError ? "text-input-text-error" : "text-text-secondary", disabled && "text-input-text-disabled")} />
              </button>
            </div>
          </PopoverAnchor>

          <PopoverContent align="start" className="w-auto p-0" aria-label={label ? `${label} calendar` : "Date range picker calendar"} onCloseAutoFocus={event => {
        event.preventDefault();
        if (lastFocused === "end") endBtnRef.current?.focus();else startBtnRef.current?.focus();
      }}>
            <Calendar mode="range" selected={selected} onSelect={handleSelect} defaultMonth={defaultMonth} disabled={disabledMatcher} numberOfMonths={numberOfMonths} classNames={numberOfMonths > 1 ? {
          months: "flex flex-row gap-6"
        } : undefined} autoFocus />
          </PopoverContent>
        </Popover>

        <input type="hidden" name={name} value={value ?? ""} />

        {hasDescription && <p id={descriptionId} role={hasError ? "alert" : undefined} className={cn("typography-component-sm-regular desktop:typography-component-md-regular", hasError ? "text-input-text-error" : "text-input-text-label")}>
            {error || description}
          </p>}
      </div>;
});
InputDateRange.displayName = "InputDateRange";